import React, { useCallback, useContext, useMemo } from 'react'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import { OfferPageActions, OfferPageFilterType, OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { hasIncludedGuestsExceeded } from 'lib/checkout/cartReservationUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import moment from 'moment'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import TextButton from 'LuxKit/Button/TextButton'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { isDepositAvailableOnOffer, getDepositAmountPercentage } from 'lib/payment/depositsUtils'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import GeoContext from 'contexts/geoContext'
import { FLIGHTS_MIN_LEAD_TIME_DAYS, FLIGHTS_MAX_LEAD_TIME_DAYS } from 'constants/flight'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { getOfferDestinationAirport } from 'selectors/offerPage/offerPageSelectors'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { useIntl } from 'react-intl'

interface Props {
  offer: App.Offer
  flightOrigin?: App.Airport
  flightDestination?: App.Airport
  isLoggedIn: boolean
  checkIn?: string
  currency: string
  room: OfferPageRoom
  hasFlights: boolean
  packageView: App.OfferPackageOptionView
  isFetching?: boolean
  defaultDepositAmountPercentage: number
  showChangeAirport?: boolean
  flightPrice?: number
  priceMode: OfferPagePriceMode
  charterBundleRoute?: App.CharterBundleRoute
  canShowDiscount?: boolean
}

function FlashHotelIndividualRoomsPriceDetails(props: Props) {
  const {
    offer,
    flightOrigin,
    flightDestination,
    checkIn,
    currency,
    room,
    hasFlights,
    packageView,
    isFetching,
    defaultDepositAmountPercentage,
    showChangeAirport = true,
    flightPrice,
    priceMode,
    charterBundleRoute,
    canShowDiscount = true,
  } = props

  const intl = useIntl()
  const {
    package: pkg,
    hotelDiscountPercent,
    bundledDiscountPercent,
    hotelPrice,
    hotelMemberPrice,
    baseMemberPrice,
    propertyFees,
  } = packageView

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const offerPageDispatch = useOfferPageDispatch()
  const showIncludedGuestsExceeded = useMemo(() => hasIncludedGuestsExceeded(room, pkg, offer) && !!pkg.roomPolicyDescription, [pkg, room, offer])

  const { loading: charterFlightLoading } = useOfferPageFlightPrice()
  const missingCharterFlightPrice = !!charterBundleRoute && !flightPrice
  const shouldShowCharterFlightErrorMessage = missingCharterFlightPrice && !charterFlightLoading

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch, pkg.roomType])

  const shouldShowDiscountPercent = canShowDiscount && hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = canShowDiscount && bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = hotelDiscountPercent > bundledDiscountPercent ? hotelDiscountPercent : bundledDiscountPercent

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])

  const departureTooEarly = useMemo(() => {
    return !!checkInMoment && checkInMoment.diff(moment(), 'days') < FLIGHTS_MIN_LEAD_TIME_DAYS
  }, [checkInMoment])

  const departureTooFarInFuture = useMemo(() => {
    return !!checkInMoment && checkInMoment.diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS
  }, [checkInMoment])

  const totalPrice = hotelPrice + (propertyFees ?? 0)
  const totalMemberPrice = useMemo(() => {
    if (hotelMemberPrice > 0) {
      return hotelMemberPrice + (propertyFees ?? 0)
    }
    return 0
  }, [hotelMemberPrice, propertyFees])
  const showMemberPrice = baseMemberPrice > 0

  const shouldShowDiscount = (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && !offer.isDiscountPillHidden
  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkInMoment, (showMemberPrice ? hotelMemberPrice : hotelPrice))

  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return [
      generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: packageView?.package?.duration }),
      generateLoyaltyPointsCalculatorBundledFlightOptions(offer, { packageView, flightPrice, skip: !hasFlights }),
    ]
  }, [offer, packageView, flightPrice, hasFlights, currentRegionCode])

  const convertToDisplay = getPriceDisplayFunction(priceMode, [room])

  return (
    <Group direction="vertical" gap={12}>
      {isDepositEnabled && <SecureWithDepositTagAndModal offerType={offer.type} depositAmountPercentage={depositAmountPercentage} depositThresholds={offer.depositThresholds} />}
      <Group direction="vertical" gap={12}>
        {!hasFlights && (
          <Group direction="vertical" gap={12}>
            <Group direction="vertical" gap={4} data-testid="pricing">
              {shouldShowDiscount && <PriceRowValueDiscount discountPercentage={highestDiscountPercent} />}
              <PriceRowPriceCaption>{pluralizeToString('night', pkg.duration)} from</PriceRowPriceCaption>
              <LuxPlusPriceStack
                size="S"
                price={convertToDisplay(totalPrice)}
                memberPrice={convertToDisplay(totalMemberPrice)}
                saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
                offerConfig={offer.luxPlus}
              />
              <PriceRowTaxesAndFees />
              {showIncludedGuestsExceeded && (
                <AdditionalGuestsPopup
                  complex={false}
                  description="+ Extra guest surcharge"
                  modalContent={pkg.roomPolicyDescription}
                />
              )}
            </Group>
            <CSSBreakpoint min="desktop">
              <LoyaltyPointsEarnWithBreakdownModal
                calculationRequests={pointsEarnCalculationRequests}
                calculationType={(!checkIn || hasFlights) ? 'estimate' : 'exact'}
              />
            </CSSBreakpoint>
          </Group>
        )}

        {hasFlights && (
          <Group direction="vertical" gap={12}>
            <Group direction="vertical" gap={4} data-testid="flights-pricing">
              {(() => {
                if (shouldShowCharterFlightErrorMessage) {
                  return <MessageBanner kind="critical">
                    {intl.formatMessage({
                      id: '2frCP+voXk',
                      defaultMessage: 'Failed to find charter flight data',
                      description: 'Error message when charter flight data cannot be found',
                    })}
                  </MessageBanner>
                }
                else if (flightOrigin?.code === flightDestination?.code) {
                  return <Group direction="vertical" gap={8}>
                    <PriceRowCaption>
                      {intl.formatMessage(
                        {
                          id: 'ui/0SxB+9L',
                          defaultMessage: 'No flights available from {airportName}. Please select another departing airport.',
                          description: 'Message when no flights available from selected airport',
                        },
                        {
                          airportName: flightOrigin?.name ?? '',
                        },
                      )}
                    </PriceRowCaption>
                    {showChangeAirport && <TextButton
                      kind="tertiary"
                      onClick={onToggleFilter}
                      data-filter="airport"
                    >
                      {intl.formatMessage({
                        id: 'ajqetAGcqb',
                        defaultMessage: 'Change airport',
                        description: 'Button text to change airport',
                      })}
                    </TextButton>}
                  </Group>
                }
                else if (departureTooEarly) {
                  return <Group direction="vertical" gap={8}>
                    <PriceRowCaption>
                      {intl.formatMessage({
                        id: '5In7m+nP8g',
                        defaultMessage: 'Departure date is too soon.',
                        description: 'Message when departure date is too soon',
                      })}
                    </PriceRowCaption>
                  </Group>
                }
                else if (departureTooFarInFuture) {
                  return <Group direction="vertical" gap={8}>
                    <PriceRowCaption>
                      {intl.formatMessage({
                        id: 'cds7S7OB+9',
                        defaultMessage: 'Flights can only be booked up to 11 months in advance.',
                        description: 'Message when departure date is too far in future',
                      })}
                    </PriceRowCaption>
                  </Group>
                }
                else if (flightPrice) {
                  return <Group direction="vertical" gap={4}>
                    <PriceRowPriceCaption startIcon={<LinePlaneFlyIcon />}>
                      {pluralizeToString('night', pkg.duration)} & flights from
                    </PriceRowPriceCaption>
                    <LuxPlusPriceStack
                      size="S"
                      price={convertToDisplay(totalPrice + flightPrice)}
                      memberPrice={convertToDisplay(totalMemberPrice > 0 ? totalMemberPrice + flightPrice : 0)}
                      saleUnit={priceMode === 'perPerson' ? 'person*' : 'total*'}
                      offerConfig={offer.luxPlus}
                    />
                    <PriceRowTaxesAndFees />
                    {showIncludedGuestsExceeded && (
                      <AdditionalGuestsPopup
                        complex={false}
                        description="+ Extra guest surcharge"
                        modalContent={pkg.roomPolicyDescription}
                      />
                    )}
                  </Group>
                }
                else {
                  return <Group direction="vertical" gap={4}>
                    <PriceRowPriceCaption startIcon={<LinePlaneFlyIcon />}>
                      {pluralizeToString('night', pkg.duration)} from
                    </PriceRowPriceCaption>
                    <LuxPlusPriceStack
                      size="S"
                      price={convertToDisplay(totalPrice)}
                      memberPrice={convertToDisplay(totalMemberPrice > 0 ? totalMemberPrice : 0)}
                      saleUnit=""
                      offerConfig={offer.luxPlus}
                    />
                    <Caption variant="large">& flights</Caption>
                    {showIncludedGuestsExceeded && (
                      <AdditionalGuestsPopup
                        complex={false}
                        description="+ Extra guest surcharge"
                        modalContent={pkg.roomPolicyDescription}
                      />
                    )}
                  </Group>
                }
              })()}
              <AtolProtectedTooltip />
            </Group>
            <CSSBreakpoint min="desktop">
              <LoyaltyPointsEarnWithBreakdownModal
                calculationRequests={pointsEarnCalculationRequests}
                calculationType={(!checkIn || hasFlights) ? 'estimate' : 'exact'}
              />
            </CSSBreakpoint>
          </Group>
        )}
        <LoadingIndicator visible={!!isFetching} floating opaque />
      </Group>
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  currency: appState.geo.currentCurrency,
  offer: state.offer,
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  isLoggedIn: isLoggedInSelector(appState),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  priceMode: state.priceMode,
  charterBundleRoute: state.charterBundleRoute,
  flightDestination: getOfferDestinationAirport(state, appState),
}), FlashHotelIndividualRoomsPriceDetails)
